import {
  ArrowDownTrayIcon,
  CurrencyDollarIcon,
  EyeIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  PhotoIcon,
  PlusIcon,
  StarIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import React, { useCallback, useEffect, useState } from 'react';
import { ADMIN_CONFIG } from '../../config/adminConfig';
import { useAdmin } from '../../contexts/AdminContext';
import { adminService } from '../../services/api';

const TemplateManagement = () => {
  const { refreshTemplates, refreshKey } = useAdmin();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [filters, setFilters] = useState({
    category: '',
    search: '',
    sortBy: 'createdAt',
    sortOrder: 'desc',
    page: 1,
    limit: ADMIN_CONFIG.pagination.defaultLimit
  });
  const [pagination, setPagination] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: '',
    subcategory: '',
    price: 0,
    isPremium: false,
    isFeatured: false,
    isActive: true,
    tags: '',
    canvaLink: '',
    dimensions: {
      width: 0,
      height: 0,
      unit: 'mm',
      dpi: ADMIN_CONFIG.template.defaultDPI
    }
  });
  const [selectedFiles, setSelectedFiles] = useState({
    thumbnail: null
  });
  const [thumbnailPreviewUrl, setThumbnailPreviewUrl] = useState(null);
  const [existingThumbnail, setExistingThumbnail] = useState(null);
  const [categories, setCategories] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const fetchCategories = useCallback(async () => {
    try {
      const response = await adminService.getActiveCategories();
      if (response.status === 'success') {
        setCategories(response.data.categories.map(cat => cat.name));
      }
    } catch (error) {
      console.error('Failed to fetch categories:', error);
      // Fallback to empty array if fetch fails
      setCategories([]);
    }
  }, []);

  const fetchTemplates = useCallback(async () => {
    try {
      setLoading(true);
      const response = await adminService.getAllTemplates(filters);
      setTemplates(response.data?.templates || []);
      const paginationData = response.data?.pagination || response.pagination || {};
      setPagination({
        page: paginationData.currentPage || paginationData.page || 1,
        pages: paginationData.totalPages || paginationData.pages || 1,
        total: paginationData.totalItems || paginationData.total || 0,
        limit: parseInt(filters.limit) || ADMIN_CONFIG.pagination.defaultLimit
      });
      setError('');
      setSuccess('');
    } catch (err) {
      setError('Failed to fetch templates');
      console.error('Error fetching templates:', err);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates, refreshKey]);

  useEffect(() => {
    return () => {
      if (thumbnailPreviewUrl) {
        URL.revokeObjectURL(thumbnailPreviewUrl);
      }
    };
  }, [thumbnailPreviewUrl]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: 1 // Reset to first page when filters change
    }));
  };

  const handlePageChange = (newPage) => {
    setFilters(prev => ({ ...prev, page: newPage }));
  };

  const handleCreateTemplate = () => {
    setEditingTemplate(null);
    setFormData({
      name: '',
      description: '',
      category: '',
      subcategory: '',
      price: 0,
      isPremium: false,
      isFeatured: false,
      isActive: true,
      tags: '',
      canvaLink: '',
      dimensions: {
        width: 0,
        height: 0,
        unit: 'mm',
        dpi: ADMIN_CONFIG.template.defaultDPI
      }
    });
    setSelectedFiles({ thumbnail: null });
    setThumbnailPreviewUrl(null);
    setExistingThumbnail(null);
    setShowModal(true);
  };

  const handleEditTemplate = (template) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name || '',
      description: template.description || '',
      category: template.category || '',
      subcategory: template.subcategory || '',
      price: template.price || 0,
      isPremium: template.isPremium || false,
      isFeatured: template.isFeatured || false,
      isActive: template.isActive !== undefined ? template.isActive : true,
      tags: template.tags ? template.tags.join(', ') : '',
      canvaLink: template.canvaLink || '',
      dimensions: {
        width: template.dimensions?.width || 0,
        height: template.dimensions?.height || 0,
        unit: template.dimensions?.unit || 'mm',
        dpi: template.dimensions?.dpi || 300
      }
    });
    setSelectedFiles({ thumbnail: null });
    setThumbnailPreviewUrl(null);
    setExistingThumbnail(template.previewImage?.url || null);
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setError('');
      setSubmitting(true);

      const width = parseFloat(formData.dimensions.width);
      const height = parseFloat(formData.dimensions.height);

      if (!width || width <= 0) {
        setError('Width must be greater than 0');
        setSubmitting(false);
        return;
      }

      if (!height || height <= 0) {
        setError('Height must be greater than 0');
        setSubmitting(false);
        return;
      }

      const templateData = {
        ...formData,
        tags: formData.tags ? formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag) : [],
        price: parseFloat(formData.price) || 0,
        dimensions: {
          ...formData.dimensions,
          width: width,
          height: height,
          dpi: parseInt(formData.dimensions.dpi) || 300
        }
      };

      let templateId;
      if (editingTemplate) {
        const response = await adminService.updateTemplate(editingTemplate._id, templateData);
        templateId = editingTemplate._id;
      } else {
        const response = await adminService.createTemplate(templateData);
        templateId = response.data?.template?._id || response.data?._id;
      }

      if (!templateId) {
        throw new Error('Failed to get template ID from response');
      }

      if (selectedFiles.thumbnail) {
        await uploadFiles(templateId);
      }

      setShowModal(false);
      setSelectedFiles({ thumbnail: null });
      if (thumbnailPreviewUrl) {
        URL.revokeObjectURL(thumbnailPreviewUrl);
      }
      setThumbnailPreviewUrl(null);
      setExistingThumbnail(null);
      setError('');
      setSuccess(`Template ${editingTemplate ? 'updated' : 'created'} successfully!`);
      await refreshTemplates();
      await fetchTemplates();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || `Failed to ${editingTemplate ? 'update' : 'create'} template`;
      setError(errorMessage);
      console.error('Error saving template:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (window.confirm('Are you sure you want to delete this template?')) {
      try {
        setError('');
        await adminService.deleteTemplate(templateId);
        setSuccess('Template deleted successfully!');
        await refreshTemplates();
        await fetchTemplates();
        setTimeout(() => setSuccess(''), 3000);
      } catch (err) {
        const errorMessage = err.response?.data?.message || err.message || 'Failed to delete template';
        setError(errorMessage);
        console.error('Error deleting template:', err);
      }
    }
  };

  const handleFileChange = (fileType, file) => {
    // Clean up previous object URL if exists
    if (fileType === 'thumbnail') {
      if (thumbnailPreviewUrl) {
        URL.revokeObjectURL(thumbnailPreviewUrl);
      }

      if (file) {
        const url = URL.createObjectURL(file);
        setThumbnailPreviewUrl(url);
      } else {
        setThumbnailPreviewUrl(null);
      }
    }

    setSelectedFiles(prev => ({
      ...prev,
      [fileType]: file
    }));
  };

  const uploadFiles = async (templateId) => {
    if (selectedFiles.thumbnail) {
      try {
        const formData = new FormData();
        formData.append('thumbnail', selectedFiles.thumbnail);
        await adminService.uploadTemplateThumbnail(templateId, formData);
      } catch (err) {
        console.error('Error uploading thumbnail:', err);
        throw new Error('Failed to upload thumbnail');
      }
    }
  };

  const formatCategory = (category) => {
    return category.split('-').map(word =>
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-16 w-16 border-4 border-green-200 border-t-green-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-4xl font-bold bg-gradient-to-r from-[#008000] to-emerald-600 bg-clip-text text-transparent">
            Template Management
          </h2>
          <p className="text-gray-600 text-lg font-medium mt-2">Manage printing templates and designs</p>
        </div>
        <button
          onClick={handleCreateTemplate}
          className="bg-gradient-to-r from-green-500 to-emerald-500 text-white px-6 py-3 rounded-xl hover:shadow-lg transition-shadow flex items-center font-bold"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Add Template
        </button>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-gradient-to-r from-red-50 to-pink-50 border border-red-200 text-red-700 px-6 py-4 rounded-xl backdrop-blur-sm">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="font-medium">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Success Display */}
      {success && (
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 text-[#008000] px-6 py-4 rounded-xl backdrop-blur-sm">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="font-medium">{success}</p>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-gradient-to-br from-white to-gray-50 backdrop-blur-sm border border-gray-200 rounded-2xl shadow-lg p-6">
        <div className="flex items-center mb-4">
          <FunnelIcon className="h-5 w-5 text-[#008000] mr-2" />
          <h3 className="text-lg font-bold text-gray-900">Filters & Search</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2">
              Category
            </label>
            <select
              value={filters.category}
              onChange={(e) => handleFilterChange('category', e.target.value)}
              className="w-full border border-gray-300 rounded-xl px-4 py-3 bg-gradient-to-r from-white to-gray-50 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-colors font-medium"
            >
              <option value="">All Categories</option>
              {categories.map(category => (
                <option key={category} value={category}>
                  {formatCategory(category)}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2">
              Search Templates
            </label>
            <div className="relative">
              <input
                type="text"
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                placeholder="Search templates..."
                className="w-full border border-gray-300 rounded-xl px-4 py-3 pl-10 bg-gradient-to-r from-white to-gray-50 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-colors font-medium"
              />
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 absolute left-3 top-3.5" />
            </div>
          </div>

          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2">
              Sort By
            </label>
            <select
              value={filters.sortBy}
              onChange={(e) => handleFilterChange('sortBy', e.target.value)}
              className="w-full border border-gray-300 rounded-xl px-4 py-3 bg-gradient-to-r from-white to-gray-50 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-colors font-medium"
            >
              <option value="createdAt">Created Date</option>
              <option value="name">Name</option>
              <option value="usage.downloads">Downloads</option>
              <option value="price">Price</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2">
              Order
            </label>
            <select
              value={filters.sortOrder}
              onChange={(e) => handleFilterChange('sortOrder', e.target.value)}
              className="w-full border border-gray-300 rounded-xl px-4 py-3 bg-gradient-to-r from-white to-gray-50 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-colors font-medium"
            >
              <option value="desc">Descending</option>
              <option value="asc">Ascending</option>
            </select>
          </div>
        </div>
      </div>

      {/* Templates Table */}
      <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Template
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Category
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Price
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Stats
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {templates.map((template) => (
                <tr key={template._id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-12 w-12">
                        {template.previewImage?.url ? (
                          <img
                            className="h-12 w-12 rounded-lg object-cover aspect-square"
                            src={template.previewImage.url}
                            alt={template.name}
                          />
                        ) : (
                          <div className="h-12 w-12 rounded-lg bg-gray-200 flex items-center justify-center">
                            <PhotoIcon className="h-6 w-6 text-gray-400" />
                          </div>
                        )}
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {template.name}
                        </div>
                        <div className="text-sm text-gray-500">
                          {template.subcategory}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {formatCategory(template.category)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <CurrencyDollarIcon className="h-4 w-4 text-gray-400 mr-1" />
                      <span className="text-sm text-gray-900">
                        {template.price === 0 ? 'Free' : `₹${template.price}`}
                      </span>
                      {template.isPremium && (
                        <StarIcon className="h-4 w-4 text-yellow-400 ml-1" />
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex flex-col space-y-1">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${template.isActive
                        ? 'bg-green-100 text-[#008000]'
                        : 'bg-red-100 text-red-800'
                        }`}>
                        {template.isActive ? 'Active' : 'Inactive'}
                      </span>
                      {template.isFeatured && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                          Featured
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center">
                        <ArrowDownTrayIcon className="h-4 w-4 text-gray-400 mr-1" />
                        {template.usage?.downloads || 0}
                      </div>
                      <div className="flex items-center">
                        <EyeIcon className="h-4 w-4 text-gray-400 mr-1" />
                        {template.usage?.orders || 0}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleEditTemplate(template)}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteTemplate(template._id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page === pagination.pages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing{' '}
                  <span className="font-medium">
                    {(pagination.page - 1) * pagination.limit + 1}
                  </span>{' '}
                  to{' '}
                  <span className="font-medium">
                    {Math.min(pagination.page * pagination.limit, pagination.total)}
                  </span>{' '}
                  of{' '}
                  <span className="font-medium">{pagination.total}</span> results
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  {Array.from({ length: pagination.pages }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${page === pagination.page
                        ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                        : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                        }`}
                    >
                      {page}
                    </button>
                  ))}
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowModal(false)}></div>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
              <form onSubmit={handleSubmit}>
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="sm:flex sm:items-start">
                    <div className="w-full">
                      <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                        {editingTemplate ? 'Edit Template' : 'Create New Template'}
                      </h3>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Basic Information */}
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">
                              Template Name *
                            </label>
                            <input
                              type="text"
                              required
                              value={formData.name}
                              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                              className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700">
                              Description
                            </label>
                            <textarea
                              value={formData.description}
                              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                              rows={3}
                              className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700">
                              Category *
                            </label>
                            <select
                              required
                              value={formData.category}
                              onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                              className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2"
                            >
                              <option value="">Select Category</option>
                              {categories.map(category => (
                                <option key={category} value={category}>
                                  {formatCategory(category)}
                                </option>
                              ))}
                            </select>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700">
                              Subcategory *
                            </label>
                            <input
                              type="text"
                              required
                              value={formData.subcategory}
                              onChange={(e) => setFormData(prev => ({ ...prev, subcategory: e.target.value }))}
                              className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700">
                              Price (₹)
                            </label>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={formData.price}
                              onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value }))}
                              className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700">
                              Tags (comma separated)
                            </label>
                            <input
                              type="text"
                              value={formData.tags}
                              onChange={(e) => setFormData(prev => ({ ...prev, tags: e.target.value }))}
                              placeholder="business, corporate, modern"
                              className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2"
                            />
                          </div>
                        </div>

                        {/* Advanced Settings */}
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">
                              Canva Link
                            </label>
                            <input
                              type="url"
                              value={formData.canvaLink}
                              onChange={(e) => setFormData(prev => ({ ...prev, canvaLink: e.target.value }))}
                              placeholder="https://www.canva.com/design/..."
                              className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2"
                            />
                          </div>

                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="block text-sm font-medium text-gray-700">
                                Width
                              </label>
                              <input
                                type="number"
                                min="0"
                                step="0.1"
                                value={formData.dimensions.width}
                                onChange={(e) => setFormData(prev => ({
                                  ...prev,
                                  dimensions: { ...prev.dimensions, width: e.target.value }
                                }))}
                                className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700">
                                Height
                              </label>
                              <input
                                type="number"
                                min="0"
                                step="0.1"
                                value={formData.dimensions.height}
                                onChange={(e) => setFormData(prev => ({
                                  ...prev,
                                  dimensions: { ...prev.dimensions, height: e.target.value }
                                }))}
                                className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2"
                              />
                            </div>
                          </div>

                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="block text-sm font-medium text-gray-700">
                                Unit
                              </label>
                              <select
                                value={formData.dimensions.unit}
                                onChange={(e) => setFormData(prev => ({
                                  ...prev,
                                  dimensions: { ...prev.dimensions, unit: e.target.value }
                                }))}
                                className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2"
                              >
                                <option value="mm">mm</option>
                                <option value="cm">cm</option>
                                <option value="inch">inch</option>
                                <option value="px">px</option>
                              </select>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700">
                                DPI
                              </label>
                              <input
                                type="number"
                                min="1"
                                value={formData.dimensions.dpi}
                                onChange={(e) => setFormData(prev => ({
                                  ...prev,
                                  dimensions: { ...prev.dimensions, dpi: e.target.value }
                                }))}
                                className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2"
                              />
                            </div>
                          </div>

                          <div className="space-y-3">
                            <div className="flex items-center">
                              <input
                                type="checkbox"
                                id="isPremium"
                                checked={formData.isPremium}
                                onChange={(e) => setFormData(prev => ({ ...prev, isPremium: e.target.checked }))}
                                className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                              />
                              <label htmlFor="isPremium" className="ml-2 block text-sm text-gray-900">
                                Premium Template
                              </label>
                            </div>

                            <div className="flex items-center">
                              <input
                                type="checkbox"
                                id="isFeatured"
                                checked={formData.isFeatured}
                                onChange={(e) => setFormData(prev => ({ ...prev, isFeatured: e.target.checked }))}
                                className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                              />
                              <label htmlFor="isFeatured" className="ml-2 block text-sm text-gray-900">
                                Featured Template
                              </label>
                            </div>

                            <div className="flex items-center">
                              <input
                                type="checkbox"
                                id="isActive"
                                checked={formData.isActive}
                                onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                                className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                              />
                              <label htmlFor="isActive" className="ml-2 block text-sm text-gray-900">
                                Active Template
                              </label>
                            </div>
                          </div>
                        </div>

                        {/* File Upload Section */}
                        <div className="col-span-1 md:col-span-2 space-y-4">
                          <h4 className="text-lg font-medium text-gray-900 border-t pt-4">Thumbnail Upload</h4>

                          <div className="max-w-md">
                            {/* Existing Thumbnail Display */}
                            {existingThumbnail && !selectedFiles.thumbnail && (
                              <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Current Thumbnail
                                </label>
                                <div className="relative inline-block">
                                  <img
                                    src={existingThumbnail}
                                    alt="Current thumbnail"
                                    className="h-32 w-auto rounded-lg border border-gray-300 object-cover"
                                  />
                                  <div className="mt-2 text-xs text-gray-500">
                                    Upload a new image to replace this thumbnail
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* Thumbnail Upload */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                <PhotoIcon className="inline h-4 w-4 mr-1" />
                                {existingThumbnail ? 'Replace Thumbnail' : 'Thumbnail Image'} (.jpg, .png)
                              </label>
                              <input
                                type="file"
                                accept=".jpg,.jpeg,.png,.webp"
                                onChange={(e) => handleFileChange('thumbnail', e.target.files[0])}
                                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-purple-50 file:text-purple-700 hover:file:bg-purple-100"
                              />
                              {selectedFiles.thumbnail && thumbnailPreviewUrl && (
                                <div className="mt-2">
                                  <p className="text-xs text-[#008000] mb-2">
                                    New file selected: {selectedFiles.thumbnail.name}
                                  </p>
                                  <div className="relative inline-block">
                                    <img
                                      src={thumbnailPreviewUrl}
                                      alt="Preview"
                                      className="h-32 w-auto rounded-lg border border-green-300 object-cover"
                                    />
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>

                          <div className="bg-blue-50 p-4 rounded-lg">
                            <div className="flex items-start">
                              <div className="flex-shrink-0">
                                <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                </svg>
                              </div>
                              <div className="ml-3">
                                <p className="text-sm text-blue-700">
                                  <strong>Thumbnail Upload Guidelines:</strong>
                                </p>
                                <ul className="mt-1 text-xs text-blue-600 list-disc list-inside space-y-1">
                                  <li>Upload a high-quality thumbnail image for the template</li>
                                  <li>Recommended size: 400x300px for optimal display</li>
                                  <li>Supported formats: JPG, PNG, WebP</li>
                                  <li>Maximum file size: 5MB</li>
                                  <li>Image will be automatically optimized and stored on Cloudinary</li>
                                </ul>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {submitting ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        {editingTemplate ? 'Updating...' : 'Creating...'}
                      </>
                    ) : (
                      editingTemplate ? 'Update Template' : 'Create Template'
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      setSubmitting(false);
                      setError('');
                      if (thumbnailPreviewUrl) {
                        URL.revokeObjectURL(thumbnailPreviewUrl);
                      }
                      setThumbnailPreviewUrl(null);
                      setExistingThumbnail(null);
                    }}
                    disabled={submitting}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TemplateManagement;
